import { Button, Group, Input, NumberField, NumberFieldProps } from "react-aria-components";

import { AlertCircle, HelpCircle } from "@icons/untitled_ui_icons";
import styles from "./NumberInput.module.css";
import { Tooltip } from "../../Tooltip/Tooltip";
import TypographyText from "../../TypographyText/TypographyText";
import Skeleton from "react-loading-skeleton";
import { AriaButton } from "../../Buttons/AriaButton/AriaButton";

export interface NumberInputProps extends NumberFieldProps {
  label?: string;
  placeholder?: string;
  description?: string;
  errorMessage?: string; //| ((validation: ValidationResult) => string);
  withSteppers?: boolean;
  helpIcon?: boolean;
  helpText?: string;
  width?: string;
  size?: "xs" | "sm" | "md";
  isLoading?: boolean;
  style?: React.CSSProperties;
}

export function NumberInput({
  label,
  placeholder,
  description,
  errorMessage,
  helpIcon,
  helpText,
  size = "sm",
  withSteppers = false,
  width,
  style,
  isLoading,
  ...props
}: NumberInputProps) {
  const sizeMapPadding = {
    xs: props.isInvalid || helpIcon ? "5px 32px 5px 11px" : "5px 11px",
    sm: props.isInvalid || helpIcon ? "7px 32px 7px 11px" : "7px 11px",
    md: props.isInvalid || helpIcon ? "9px 32px 9px 13px" : "9px 13px",
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {label && (
        <TypographyText size="sm" weight="semibold" color="var(--gray-700)">
          {!isLoading && label}
          {isLoading && <Skeleton width={width ?? "100%"} />}
        </TypographyText>
      )}
      {isLoading && <Skeleton width={width ?? "100%"} height={size === "xs" ? 36 : size === "sm" ? 40 : 44} />}
      {!isLoading && (
        <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
          <NumberField aria-label="Number input" {...props} className={styles.reactAriaNumberField} style={{ width: width ?? "100%", ...style }}>
            {withSteppers ? (
              <Group className={styles.reactAriaGroup}>
                <AriaButton slot="decrement" className={styles.reactAriaIncrementButton}>
                  -
                </AriaButton>
                <Input placeholder={placeholder} style={{ padding: sizeMapPadding[size] }} className={styles.reactAriaInputWithSteppers} />
                <AriaButton slot="increment" className={styles.reactAriaIncrementButton}>
                  +
                </AriaButton>
              </Group>
            ) : (
              <Input placeholder={placeholder} style={{ padding: sizeMapPadding[size] }} className={styles.reactAriaInput} />
            )}
          </NumberField>
          <div
            style={{ position: "absolute", right: withSteppers ? "51px" : "11px", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            {props.isInvalid && <AlertCircle width={16} height={16} color="var(--error-500)" />}
            {helpIcon && !props.isInvalid && (
              <Tooltip
                insideToolTip={
                  <TypographyText size="sm" weight="regular" color="var(--gray-50)">
                    {helpText}
                  </TypographyText>
                }
                triggerElement={<HelpCircle width={16} height={16} color="var(--gray-500)" />}
              />
            )}
          </div>
        </div>
      )}
      {description && (
        <TypographyText size="sm" weight="regular" color="var(--gray-600)">
          {!isLoading && description}
          {isLoading && <Skeleton width={width ?? "100%"} />}
        </TypographyText>
      )}
      {props.isInvalid && (
        <TypographyText size="sm" weight="regular" color="var(--error-500)">
          {errorMessage}
        </TypographyText>
      )}
    </div>
  );
}
